import {useMutation, useQueryClient} from '@tanstack/react-query';
import conversationMessagesUpdaters from '@/api/updaters/conversationMessages';
import {
  invalidateUnreadUiQueries,
  restoreUnreadAfterMarkUnread,
} from '../use-mark-all-read/clearUnreadAfterMarkAllRead';

export type MarkUnreadVariables = {
  messageId: string;
  conversationId?: string;
  campaignId?: string;
};

export type UseMarkUnreadOptions<TSnapshot = unknown> = {
  onMutate?: (variables: MarkUnreadVariables) => TSnapshot;
  onError?: (context: TSnapshot) => void;
};

function useMarkUnread<TSnapshot = unknown>({
  onMutate,
  onError,
}: UseMarkUnreadOptions<TSnapshot> = {}) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, MarkUnreadVariables, TSnapshot>({
    mutationKey: ['conversations', 'markUnread'],
    mutationFn: ({messageId}) =>
      conversationMessagesUpdaters.markUnread(messageId),
    onMutate: onMutate ? (variables) => onMutate(variables) : undefined,
    onError: onError
      ? (_err, _vars, context) => {
          if (context !== undefined) {
            onError(context);
          }
        }
      : undefined,
    onSuccess: (_data, {conversationId, campaignId, messageId}) => {
      // ConversationChat manages its own details cache and omits conversationId.
      if (!conversationId) {
        return;
      }
      restoreUnreadAfterMarkUnread(queryClient, {
        conversationId,
        campaignId,
        messageId,
      });
      invalidateUnreadUiQueries(queryClient, {conversationId, campaignId});
    },
  });
}

export default useMarkUnread;
